import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import StatCard from '../../lib/components/StatCard';
import {
    getChecklistSummary,
    getFinalizationStats,
    mockFinalizationChecklists,
    mockYearGroupStatus,
    statusConfig,
    type YearGroupFinalizationStatus,
} from './MockData';

const Finalization: React.FC = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedYearGroup, setSelectedYearGroup] = useState<YearGroupFinalizationStatus | null>(null);
    const [finalizeAllDialogOpen, setFinalizeAllDialogOpen] = useState(false);

    const stats = useMemo(() => getFinalizationStats(), []);
    const checklistSummary = useMemo(() => getChecklistSummary(), []);

    const handleFinalize = (yearGroup: YearGroupFinalizationStatus) => {
        setSelectedYearGroup(yearGroup);
        setConfirmDialogOpen(true);
    };

    const confirmFinalization = () => {
        // In real app, this would call the store
        setConfirmDialogOpen(false);
        setSelectedYearGroup(null);
    };

    const handleFinalizeAll = () => {
        setFinalizeAllDialogOpen(true);
    };

    const confirmFinalizeAll = () => {
        // In real app, this would call the store
        setFinalizeAllDialogOpen(false);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'finalized':
                return <LockIcon color="primary" />;
            case 'ready':
                return <CheckCircleIcon color="success" />;
            case 'blocked':
                return <BlockIcon color="error" />;
            default:
                return <PendingIcon color="action" />;
        }
    };

    const getChecklistIcon = (status: string) => {
        switch (status) {
            case 'passed':
                return <CheckCircleIcon color="success" fontSize="small" />;
            case 'failed':
                return <ErrorIcon color="error" fontSize="small" />;
            case 'warning':
                return <WarningIcon color="warning" fontSize="small" />;
            default:
                return <PendingIcon color="action" fontSize="small" />;
        }
    };

    const readyToFinalize = mockYearGroupStatus.filter((y) => y.canFinalize);
    const canFinalizeAll = readyToFinalize.length > 0 && checklistSummary.failed === 0;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Finalization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Review and lock timetables by year group to prevent further modifications
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<LockIcon />}
                        disabled={!canFinalizeAll}
                        onClick={handleFinalizeAll}
                    >
                        Finalize All Ready ({readyToFinalize.length})
                    </Button>
                    <IconButton>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Progress Overview */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Finalization Progress</Typography>
                        <Typography variant="h4" color="primary">
                            {stats.overallProgress}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={stats.overallProgress}
                        sx={{ height: 12, borderRadius: 1, mb: 2 }}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="primary.main">
                                    {stats.finalizedCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Finalized
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="success.main">
                                    {readyToFinalize.length}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Ready
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="warning.main">
                                    {stats.pendingCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Pending
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" color="error.main">
                                    {stats.blockedCount}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Blocked
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        stat={{
                            label: 'Checklist Passed',
                            value: checklistSummary.passed,
                            color: 'success',
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        stat={{
                            label: 'Warnings',
                            value: checklistSummary.warnings,
                            color: 'warning',
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        stat={{
                            label: 'Failed Checks',
                            value: checklistSummary.failed,
                            color: 'error',
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        stat={{
                            label: 'Total Checks',
                            value: checklistSummary.total,
                            color: 'info',
                        }}
                    />
                </Grid>
            </Grid>

            {/* Tabs */}
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                <Tab label="Year Groups" />
                <Tab label="Finalization Checklist" />
            </Tabs>

            {/* Year Groups Tab */}
            {activeTab === 0 && (
                <Grid container spacing={2}>
                    {mockYearGroupStatus.map((yearGroup) => (
                        <Grid item key={yearGroup.id} xs={12} sm={6} md={4}>
                            <Card
                                sx={{
                                    height: '100%',
                                    bgcolor: statusConfig[yearGroup.status]?.bgColor,
                                    border: yearGroup.status === 'finalized' ? `2px solid ${theme.palette.primary.main}` : undefined,
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusIcon(yearGroup.status)}
                                            <Typography variant="h6">{yearGroup.displayName}</Typography>
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={statusConfig[yearGroup.status]?.label}
                                            color={statusConfig[yearGroup.status]?.color}
                                        />
                                    </Box>

                                    <Grid container spacing={1} sx={{ mb: 2 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Lessons
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {yearGroup.lessonCount}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Conflicts
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                fontWeight="medium"
                                                color={yearGroup.conflictCount > 0 ? 'error.main' : 'success.main'}
                                            >
                                                {yearGroup.conflictCount}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Coverage
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                fontWeight="medium"
                                                color={yearGroup.coveragePercentage >= 100 ? 'success.main' : 'warning.main'}
                                            >
                                                {yearGroup.coveragePercentage}%
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Satisfaction
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium">
                                                {yearGroup.teacherSatisfaction}%
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {yearGroup.blockedReason && (
                                        <Alert severity="error" sx={{ mb: 2, py: 0 }}>
                                            <Typography variant="caption">{yearGroup.blockedReason}</Typography>
                                        </Alert>
                                    )}

                                    {yearGroup.finalizedAt && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Finalized by {yearGroup.finalizedByName} on {formatDate(yearGroup.finalizedAt)}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Last modified: {formatDate(yearGroup.lastModified)}
                                        </Typography>
                                        {yearGroup.canFinalize && (
                                            <Tooltip title="Finalize this year group">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<LockIcon />}
                                                    onClick={() => handleFinalize(yearGroup)}
                                                >
                                                    Finalize
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Checklist Tab */}
            {activeTab === 1 && (
                <Grid container spacing={2}>
                    {mockFinalizationChecklists.map((checklist) => (
                        <Grid item key={checklist.id} xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        {checklist.category}
                                    </Typography>
                                    <List dense>
                                        {checklist.items.map((item) => (
                                            <ListItem key={item.id} sx={{ py: 0.5 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>{getChecklistIcon(item.status)}</ListItemIcon>
                                                <ListItemText
                                                    primary={item.label}
                                                    secondary={item.details}
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        color: item.status === 'failed' ? 'error.main' : 'text.primary',
                                                    }}
                                                />
                                                {item.affectedCount !== undefined && item.affectedCount > 0 && (
                                                    <Chip size="small" label={`${item.affectedCount} affected`} color="warning" />
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    {/* Summary */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Pre-Finalization Summary
                            </Typography>
                            {checklistSummary.failed > 0 ? (
                                <Alert severity="error">
                                    {checklistSummary.failed} critical issue(s) must be resolved before finalizing any year groups.
                                </Alert>
                            ) : checklistSummary.warnings > 0 ? (
                                <Alert severity="warning">
                                    {checklistSummary.warnings} warning(s) found. You may proceed with finalization, but review is
                                    recommended.
                                </Alert>
                            ) : (
                                <Alert severity="success">
                                    All checks passed! You can safely finalize all ready year groups.
                                </Alert>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Finalize Single Year Group Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockIcon color="primary" />
                        Confirm Finalization
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedYearGroup && (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Once finalized, the timetable for <strong>{selectedYearGroup.displayName}</strong> will be
                                locked and cannot be edited without creating a new version.
                            </Alert>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                This action will:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Lock all lessons for this year group" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Make timetable visible in portal" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary="Enable export and distribution" />
                                </ListItem>
                            </List>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                Lessons: {selectedYearGroup.lessonCount} • Coverage: {selectedYearGroup.coveragePercentage}%
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmFinalization} startIcon={<LockIcon />}>
                        Finalize {selectedYearGroup?.displayName}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Finalize All Dialog */}
            <Dialog open={finalizeAllDialogOpen} onClose={() => setFinalizeAllDialogOpen(false)}>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LockIcon color="primary" />
                        Finalize All Ready Year Groups
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        You are about to finalize <strong>{readyToFinalize.length} year groups</strong> at once. This action
                        cannot be undone without creating a new timetable version.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        The following year groups will be finalized:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {readyToFinalize.map((yg) => (
                            <Chip key={yg.id} label={yg.displayName} color="success" />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalizeAllDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmFinalizeAll} startIcon={<LockIcon />}>
                        Finalize All
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Finalization;

